import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { formatMoney, formatRange } from "../lib/format";
import { useConfirm, useAlert } from "../components/Confirm";
import { TypeBadge } from "../components/EstimateTable";

export function AdminLanding() {
  const [stage, setStage] = useState<"queue" | "history">("queue");
  const list = useQuery({ queryKey: ["approvals", stage], queryFn: () => api.approvalsList(stage) });
  const history = useQuery({ queryKey: ["approvals", "history"], queryFn: () => api.approvalsList("history"), enabled: stage === "queue" });
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [bulkReason, setBulkReason] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const confirm = useConfirm();
  const alertModal = useAlert();
  const qc = useQueryClient();

  const rows: any[] = list.data?.estimates ?? [];
  const selectedIds = Object.keys(selected).filter((id) => selected[id]);

  // Average approval SLA (history)
  const slaHours = useMemo(() => {
    const approved = (history.data?.estimates ?? []).filter((r: any) => r.status === "approved" && r.submitted_at && r.approved_at);
    if (!approved.length) return null;
    const total = approved.reduce((s: number, r: any) => {
      const ms = new Date(r.approved_at).getTime() - new Date(r.submitted_at).getTime();
      return s + ms;
    }, 0);
    return total / approved.length / (1000 * 60 * 60);
  }, [history.data]);

  function toggleAll() {
    if (selectedIds.length === rows.length) setSelected({});
    else setSelected(Object.fromEntries(rows.map((r) => [r.id, true])));
  }

  async function bulkDo(action: "approve" | "reject" | "revert") {
    if (!selectedIds.length) return;
    if (action === "reject" && !bulkReason.trim()) {
      await alertModal({ title: "Reason required", body: "Please enter a reason to bulk-reject.", confirmLabel: "OK" });
      return;
    }
    const ok = await confirm({
      title: `${action[0].toUpperCase()}${action.slice(1)} ${selectedIds.length} estimate${selectedIds.length === 1 ? "" : "s"}?`,
      body: `This will ${action} ${selectedIds.length} submitted estimate${selectedIds.length === 1 ? "" : "s"} in one go. Claimants will be notified individually.`,
      confirmLabel: `${action[0].toUpperCase()}${action.slice(1)} ${selectedIds.length}`,
      danger: action === "reject",
      warning: action === "revert",
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const r = await api.bulkAction(action, selectedIds, bulkReason || undefined);
      await alertModal({
        title: "Bulk action complete",
        body: `${r.successes} succeeded, ${r.failures} failed.`,
        confirmLabel: "OK",
      });
      setSelected({});
      setBulkReason("");
      await qc.invalidateQueries({ queryKey: ["approvals"] });
    } finally { setBulkBusy(false); }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-semibold text-liaise-navy">Approvals</h1>
          <p className="text-slate-500 mt-1">Holiday pricing estimates awaiting your review.</p>
        </div>
        {slaHours != null && stage === "queue" && (
          <div className="text-xs text-slate-500">
            Avg approval time: <b className="text-slate-700">{slaHours < 24 ? `${slaHours.toFixed(1)} hrs` : `${(slaHours / 24).toFixed(1)} days`}</b>
          </div>
        )}
      </div>

      <div className="flex gap-2 text-sm">
        <button onClick={() => setStage("queue")}
          className={`px-3 py-1.5 rounded-lg ${stage === "queue" ? "bg-liaise-navy text-white" : "bg-white border border-slate-200"}`}>
          Queue {rows.length > 0 && stage === "queue" && <span className="ml-1 opacity-70">({rows.length})</span>}
        </button>
        <button onClick={() => setStage("history")}
          className={`px-3 py-1.5 rounded-lg ${stage === "history" ? "bg-liaise-navy text-white" : "bg-white border border-slate-200"}`}>
          History
        </button>
      </div>

      {/* Bulk action bar */}
      {stage === "queue" && selectedIds.length > 0 && (
        <div className="bg-liaise-navy text-white rounded-2xl p-4 flex flex-wrap items-center gap-3 text-sm sticky top-0 z-10">
          <div className="font-medium">{selectedIds.length} selected</div>
          <input type="text" value={bulkReason} onChange={(e) => setBulkReason(e.target.value)} placeholder="Reason (required for reject, optional otherwise)"
            className="flex-1 min-w-[180px] px-3 py-1.5 rounded bg-white/10 placeholder:text-white/50 text-white text-sm focus:outline-none focus:bg-white/20" />
          <button onClick={() => bulkDo("approve")} disabled={bulkBusy}
            className="px-3 py-1.5 rounded bg-liaise-green text-white text-sm font-medium disabled:opacity-50">Approve all</button>
          <button onClick={() => bulkDo("reject")} disabled={bulkBusy}
            className="px-3 py-1.5 rounded bg-liaise-red text-white text-sm font-medium disabled:opacity-50">Reject all</button>
          <button onClick={() => setSelected({})} className="text-xs text-white/70 hover:text-white underline">Clear</button>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {list.isLoading && <div className="p-6 text-slate-500 text-sm">Loading...</div>}
        {list.error && <div className="p-6 text-red-600 text-sm">{(list.error as Error).message}</div>}
        {list.data && rows.length === 0 && <div className="p-10 text-center text-slate-500 text-sm">Nothing here. 👌</div>}
        {list.data && rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                {stage === "queue" && (
                  <th className="px-4 py-2 w-8">
                    <input type="checkbox" checked={selectedIds.length === rows.length} onChange={toggleAll} />
                  </th>
                )}
                <th className="text-left px-4 py-2">Ref</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2">Claimant</th>
                <th className="text-left px-4 py-2">PWS</th>
                <th className="text-left px-4 py-2">Home / Funder</th>
                <th className="text-left px-4 py-2">Dates</th>
                <th className="text-right px-4 py-2">Total</th>
                {stage === "queue" && <th className="text-left px-4 py-2">Age</th>}
                <th className="text-left px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e: any) => (
                <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50">
                  {stage === "queue" && (
                    <td className="px-4 py-2">
                      <input type="checkbox" checked={!!selected[e.id]}
                        onChange={(ev) => setSelected((s) => ({ ...s, [e.id]: ev.target.checked }))} />
                    </td>
                  )}
                  <td className="px-4 py-2">
                    <Link to={`/approvals/${e.id}`} className="text-liaise-blue hover:underline">{e.request_number}</Link>
                  </td>
                  <td className="px-4 py-2"><TypeBadge type={e.type ?? "holiday"} /></td>
                  <td className="px-4 py-2">
                    {e.claimant_entra_oid ? (
                      <Link to={`/claimant/${e.claimant_entra_oid}`} className="text-liaise-blue hover:underline">{e.claimant_display_name}</Link>
                    ) : e.claimant_display_name}
                  </td>
                  <td className="px-4 py-2">
                    {e.pws_id ? (
                      <Link to={`/pws/${e.pws_id}`} className="text-liaise-blue hover:underline">{e.pws_name ?? e.pws_initials}</Link>
                    ) : e.pws_initials}
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {e.home_id ? <Link to={`/home/${e.home_id}`} className="text-liaise-blue hover:underline">{e.home_name}</Link> : e.home_name ?? "-"}
                    {" · "}
                    {e.funder_id ? <Link to={`/funder/${e.funder_id}`} className="text-liaise-blue hover:underline text-slate-500">{e.funder_name}</Link> : <span className="text-slate-500">{e.funder_name ?? "-"}</span>}
                  </td>
                  <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{formatRange(e.start_date, e.end_date)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatMoney(e.total_amount)}</td>
                  {stage === "queue" && (
                    <td className="px-4 py-2 text-xs">
                      <Age submittedAt={e.submitted_at} />
                    </td>
                  )}
                  <td className="px-4 py-2 text-xs capitalize">{e.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Age({ submittedAt }: { submittedAt?: string | null }) {
  if (!submittedAt) return <span className="text-slate-400">—</span>;
  const ms = Date.now() - new Date(submittedAt).getTime();
  const hours = ms / (1000 * 60 * 60);
  const days = hours / 24;
  let label: string;
  let cls = "text-slate-500";
  if (hours < 1) label = "Just now";
  else if (hours < 24) label = `${Math.round(hours)} hr${Math.round(hours) === 1 ? "" : "s"} ago`;
  else label = `${Math.round(days)} day${Math.round(days) === 1 ? "" : "s"} ago`;
  if (days >= 3) cls = "text-orange-700 font-medium";
  if (days >= 7) cls = "text-red-700 font-semibold";
  return <span className={cls}>{label}</span>;
}
